import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Book, Gift, Cross, Sparkles, ArrowRight } from "lucide-react";
import cartiReligioaseImg from "@/assets/category-carti-religioase-new.png";
import suvenirReligiosImg from "@/assets/category-suvenir-religios-new.jpg";
import cadouriUnicatImg from "@/assets/category-cadouri-unicat-new.jpg";

const ProductCategories = () => {
  const navigate = useNavigate();
  
  const categories = [
    {
      title: "Cărți religioase",
      description: "Biblii, Evanghelii, Psaltirii legate în piele naturală cu tehnici tradiționale",
      image: cartiReligioaseImg,
      icon: Book,
      link: "/magazin?category=carte-religioasa-legata-in-piele",
      gradient: "from-burgundy-dark/90 via-burgundy/80 to-primary/70"
    },
    {
      title: "Suvenir religios",
      description: "Icoane, obiecte din piele și cadouri cu tematică spirituală ortodoxă",
      image: suvenirReligiosImg,
      icon: Cross,
      link: "/magazin?category=suvenir-religios",
      gradient: "from-primary/90 via-accent/80 to-gold/70"
    },
    {
      title: "Cadouri unicat",
      description: "Albume personalizate, coperți din piele și creații unice realizate manual",
      image: cadouriUnicatImg,
      icon: Gift,
      link: "/magazin?category=cadouri-unicat",
      gradient: "from-accent/90 via-burgundy-dark/80 to-primary/70"
    },
  ];

  return (
    <section id="magazin" className="py-20 bg-gradient-to-b from-background via-cream/20 to-background relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12 sm:mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary/10 rounded-full mb-4 sm:mb-6">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
            <span className="text-primary font-semibold text-xs sm:text-sm">Explorează colecția</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-4 sm:mb-6 font-playfair px-2">
            Categorii de produse
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto font-lora leading-relaxed px-4">
            Descoperă colecția noastră de cărți religioase legate manual în piele naturală, 
            obiecte spirituale și cadouri personalizate create cu pasiune
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {categories.map((category, index) => {
            const Icon = category.icon;
            return (
              <Card
                key={category.title}
                onClick={() => navigate(category.link)}
                className="group overflow-hidden cursor-pointer bg-card/80 backdrop-blur-sm border-2 border-border/50 hover:border-primary/30 transition-all duration-500 hover-scale hover-glow animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden">
                  <img
                    src={category.image}
                    alt={`${category.title} - cărți și obiecte religioase legate în piele Idaco`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient} opacity-80 group-hover:opacity-90 transition-opacity duration-500`} />
                  
                  {/* Icon */}
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 bg-cream/20 backdrop-blur-md rounded-2xl flex items-center justify-center border border-cream/30 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-cream" />
                  </div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 md:p-8 text-cream">
                    <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 group-hover:text-gold transition-colors duration-300 font-playfair">
                      {category.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-cream/90 leading-relaxed font-lora mb-3 sm:mb-4">
                      {category.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-gold font-semibold group-hover:gap-3 transition-all duration-300">
                      <span className="text-xs sm:text-sm">Explorează</span>
                      <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>

                  {/* Decorative corner accent */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>
              </Card>
            );
          })}
        </div>

        <div className="text-center animate-fade-in px-4" style={{ animationDelay: '0.6s' }}>
          <Button 
            size="lg" 
            onClick={() => navigate('/magazin')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 rounded-full shadow-2xl hover-scale hover-glow group w-full sm:w-auto"
          >
            <Book className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Vezi toate produsele
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProductCategories;
